import { ExternalLink, FileText, LibraryBig } from 'lucide-react';
import { Badge, Card, CardBody, EmptyState, ErrorState, LoadingState, PageHeader } from '@tutor/ui';
import { useResources } from '../../hooks/queries';
import { formatDate } from '../../lib/format';

export function ResourcesPage({ perspective }: { perspective: 'tutor' | 'student' }) {
  const { data, isLoading, isError } = useResources();

  return (
    <>
      <PageHeader
        title="Resources"
        description={
          perspective === 'tutor'
            ? 'Material you have shared with your students.'
            : 'Notes, worksheets and links shared by your tutors.'
        }
      />

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState description="We could not load resources." />
      ) : (data?.length ?? 0) === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              title="No resources yet"
              description={
                perspective === 'tutor'
                  ? 'Upload a worksheet or share a link to get started.'
                  : 'Your tutors have not shared anything yet.'
              }
              icon={<LibraryBig className="h-5 w-5" aria-hidden />}
            />
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {data!.map((resource) => {
            const href = resource.externalUrl ?? resource.file?.url;

            return (
              <a
                key={resource.id}
                href={href ?? '#'}
                target="_blank"
                rel="noreferrer"
                className="surface flex flex-col p-5 transition-colors hover:border-brand-300"
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                    <FileText className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-slate-900">{resource.title}</p>
                    {resource.subjectName && (
                      <Badge tone="brand" className="mt-1">
                        {resource.subjectName}
                      </Badge>
                    )}
                  </div>
                  <ExternalLink className="h-4 w-4 shrink-0 text-slate-300" aria-hidden />
                </div>

                {resource.description && (
                  <p className="mt-3 line-clamp-2 text-sm text-slate-600">{resource.description}</p>
                )}

                <p className="mt-auto pt-3 text-xs text-slate-400">Added {formatDate(resource.createdAt)}</p>
              </a>
            );
          })}
        </div>
      )}
    </>
  );
}
